import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Leaf, ShieldCheck, TrendingUp, Sparkles, Wallet, Wrench, LineChart as LineChartIcon,
  Factory, ChevronDown, Sun, BatteryCharging, Zap, Cloud, ArrowRight, Handshake,
  Building2, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, BarChart, Bar, Area, AreaChart, ReferenceLine, Legend,
  ComposedChart,
} from "recharts";

function money(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}m`;
  return `$${Math.round(n).toLocaleString()}`;
}
function num(v, unit = "") {
  if (v == null || v === "" || Number.isNaN(v)) return "—";
  return `${Number(v).toLocaleString()}${unit}`;
}

export default function ProposalPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/proposals/public/${token}`);
        if (mounted) setState({ loading: false, error: "", data });
      } catch (e) {
        if (mounted) setState({ loading: false, error: e.response?.status === 404 ? "Proposal not found or link expired." : "Something went wrong.", data: null });
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  if (state.loading) return <FullPageMessage title="Loading your proposal…" />;
  if (state.error) return <FullPageMessage title={state.error} subtitle="Please contact your Be Green Group representative." />;

  const p = state.data.proposal;
  const proj = state.data.projection;

  return (
    <div className="min-h-screen bg-white text-forest">
      <TopBar proposalNumber={p.proposal_number} />
      <Hero p={p} proj={proj} />
      <BigNumbers p={p} proj={proj} />
      <BeforeAfter p={p} />
      <AboutUs />
      <ProjectionSection p={p} proj={proj} />
      <MonthlyEconomicsSection proj={proj} />
      <BreakdownSection p={p} proj={proj} />
      <Assumptions p={p} proj={proj} />
      <NextSteps p={p} />
      <Footer p={p} />
    </div>
  );
}

/* ---------- TOP BAR ---------- */
function TopBar({ proposalNumber }) {
  return (
    <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-lg border-b border-black/5" data-testid="proposal-topbar">
      <div className="container-x mx-auto max-w-7xl flex items-center justify-between h-16">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center h-9 w-9 rounded-lg bg-forest text-white">
            <Leaf size={18} strokeWidth={2.2}/>
          </span>
          <div>
            <p className="font-heading font-semibold text-forest text-[15px] leading-tight">Be Green Group</p>
            <p className="text-[11px] text-neutral-500 -mt-0.5">Indicative Business Energy Proposal</p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-[12px] text-neutral-500">
          <span className="rounded-full bg-mist px-3 py-1 text-forest font-semibold">Proposal · {proposalNumber}</span>
          <span className="hidden md:inline">Prepared privately for your business</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero({ p, proj }) {
  return (
    <section className="relative overflow-hidden bg-forest-gradient text-white grain" data-testid="proposal-hero">
      <div className="container-x mx-auto max-w-7xl pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-[1.35fr_1fr] gap-14 items-center relative z-10">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3.5 py-1.5 text-[12.5px] font-medium mb-6">
            <span className="h-2 w-2 rounded-full bg-bright" /> Prepared for {p.business_name}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-[1.02] tracking-tight">
            Your indicative business <br className="hidden md:block"/>
            <span className="text-bright">energy plan</span>.
          </h1>
          <p className="mt-6 text-white/75 text-[16.5px] md:text-lg max-w-xl leading-relaxed">
            A no-upfront commercial solar and battery plan designed for {p.site_address || "your site"}. Be Green
            Group installs, owns and maintains the system — you pay a simple flat rate per kWh for the energy your
            business uses from it.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-5 text-[13.5px] text-white/70">
            <Stat label="System size" value={num(p.system_size_kw, " kW")} />
            <Stat label="Battery" value={num(p.battery_size_kwh, " kWh")} />
            <Stat label="PPA rate" value={p.proposed_ppa_rate_cents ? `${p.proposed_ppa_rate_cents} c/kWh` : "—"} />
            <Stat label="Term" value={num(p.proposed_term_years, " yrs")} />
          </div>
        </div>

        <div className="relative">
          <div className="rounded-3xl bg-white/[0.06] border border-white/15 p-6 md:p-8 backdrop-blur">
            <p className="text-[11px] tracking-widest uppercase text-white/60 font-semibold">Estimated {proj.years}-year savings</p>
            <p className="mt-2 font-heading text-4xl md:text-5xl font-semibold text-bright leading-none">
              {money(proj.totals.total_savings)}
            </p>
            <p className="text-[12.5px] text-white/60 mt-2">Cumulative, indicative — subject to final agreement.</p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-[13px]">
              <MiniStat label="Grid cost (10 yrs)" value={money(proj.totals.grid_only_total_cost)} />
              <MiniStat label="With Be Green" value={money(proj.totals.new_total_cost)} />
              <MiniStat label="CO₂ avoided" value={`${proj.totals.total_co2_tonnes} t`} />
              <MiniStat label="Annual savings yr 1" value={money(proj.rows?.[0]?.annual_savings)} />
            </div>
          </div>
          <div className="absolute -bottom-5 -right-5 md:-right-8 bg-bright text-[hsl(150_40%_8%)] rounded-2xl px-5 py-4 shadow-xl">
            <p className="text-[10.5px] uppercase tracking-widest font-semibold">No upfront cost</p>
            <p className="font-heading font-semibold text-lg leading-tight">System owned by Be Green Group</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[11px] tracking-widest uppercase text-white/50 font-semibold">{label}</p>
      <p className="text-white font-heading font-semibold text-lg">{value}</p>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2.5">
      <p className="text-[10.5px] uppercase tracking-widest text-white/55">{label}</p>
      <p className="mt-1 font-heading font-semibold text-white">{value}</p>
    </div>
  );
}

/* ---------- BIG NUMBERS ---------- */
function BigNumbers({ p, proj }) {
  const paybackYear = useMemo(() => {
    let cum = 0;
    for (const r of proj.rows) {
      cum += r.annual_savings;
      if (cum > 0) return r.year;
    }
    return null;
  }, [proj.rows]);

  return (
    <section className="container-x mx-auto max-w-7xl py-16 md:py-20 grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="proposal-numbers">
      <NumberTile icon={Wallet} tone="mist" eyebrow="Annual grid cost today"
                  value={money(p.current_annual_cost)} note="Your baseline before any changes" />
      <NumberTile icon={Sparkles} tone="bright" eyebrow="Estimated year-1 savings"
                  value={money(proj.rows?.[0]?.annual_savings)} note="Indicative — subject to final assessment" />
      <NumberTile icon={TrendingUp} tone="mist" eyebrow={`Cumulative ${proj.years}-year savings`}
                  value={money(proj.totals.total_savings)} note="Compared to staying on grid supply" />
      <NumberTile icon={LineChartIcon} tone="mist" eyebrow="Estimated payback"
                  value={paybackYear ? `Year ${paybackYear}` : "—"} note="No upfront system cost — savings from day 1" />
    </section>
  );
}
function NumberTile({ icon: Icon, eyebrow, value, note, tone = "mist" }) {
  const isBright = tone === "bright";
  return (
    <div className={`relative overflow-hidden rounded-2xl p-6 border ${isBright ? "bg-bright text-[hsl(150_40%_8%)] border-bright" : "bg-mist border-black/5"}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[11px] tracking-widest uppercase font-semibold ${isBright ? "text-forest/80" : "text-forest/70"}`}>{eyebrow}</span>
        <Icon size={18} className={isBright ? "text-forest" : "text-forest"} />
      </div>
      <p className={`mt-4 font-heading text-3xl md:text-4xl font-semibold ${isBright ? "text-forest" : "text-forest"}`}>
        {value}
      </p>
      <p className={`mt-2 text-[12.5px] ${isBright ? "text-forest/80" : "text-neutral-600"}`}>{note}</p>
    </div>
  );
}

/* ---------- BEFORE / AFTER ---------- */
function BeforeAfter({ p }) {
  return (
    <section className="container-x mx-auto max-w-7xl pb-8 md:pb-14 grid lg:grid-cols-2 gap-5" data-testid="proposal-before-after">
      <SideCard title="Today: 100% grid" tone="light" testId="side-today">
        <Row icon={Zap} label="Annual electricity usage" value={num(p.current_annual_kwh, " kWh")} />
        <Row icon={LineChartIcon} label="Estimated average rate" value={p.current_avg_rate_cents ? `${p.current_avg_rate_cents} c / kWh` : "—"} />
        <Row icon={Wallet} label="Estimated annual cost" value={money(p.current_annual_cost)} />
        <Row icon={Cloud} label="Grid mix (typical)" value="Predominantly fossil-fuel generated" />
      </SideCard>
      <SideCard title="With Be Green: solar + battery" tone="forest" testId="side-with">
        <Row dark icon={Sun} label="Solar system" value={num(p.system_size_kw, " kW")} />
        <Row dark icon={BatteryCharging} label="Battery storage" value={num(p.battery_size_kwh, " kWh")} />
        <Row dark icon={Zap} label="Energy supplied from system" value={num(p.estimated_energy_supplied_kwh, " kWh / yr")} />
        <Row dark icon={LineChartIcon} label="Flat PPA rate" value={p.proposed_ppa_rate_cents ? `${p.proposed_ppa_rate_cents} c / kWh` : "—"} />
      </SideCard>
    </section>
  );
}
function SideCard({ title, tone, children, testId }) {
  const isForest = tone === "forest";
  return (
    <div className={`relative overflow-hidden rounded-3xl border p-7 md:p-9 grain ${isForest ? "bg-forest-gradient text-white border-white/10" : "bg-mist text-forest border-black/5"}`} data-testid={testId}>
      <h3 className={`font-heading text-2xl font-semibold ${isForest ? "text-white" : "text-forest"}`}>{title}</h3>
      <div className="mt-5 space-y-2 relative z-10">{children}</div>
    </div>
  );
}
function Row({ icon: Icon, label, value, dark }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-2.5 border-b ${dark ? "border-white/10" : "border-black/5"} last:border-b-0`}>
      <span className={`flex items-center gap-2.5 text-[13.5px] ${dark ? "text-white/75" : "text-neutral-600"}`}>
        <span className={`grid place-items-center h-7 w-7 rounded-lg ${dark ? "bg-white/10 text-bright" : "bg-white text-forest"}`}>
          <Icon size={13} strokeWidth={2.2} />
        </span>
        {label}
      </span>
      <span className={`text-[14px] font-semibold text-right ${dark ? "text-white" : "text-forest"}`}>{value}</span>
    </div>
  );
}

/* ---------- ABOUT US ---------- */
function AboutUs() {
  const values = [
    { icon: Handshake, t: "Long-term partnerships", b: "We stay involved for the full term of the agreement — not just installation." },
    { icon: Building2, t: "Practical commercial outcomes", b: "Focused on what CFOs and operators actually care about." },
    { icon: Wrench, t: "System ownership & maintenance", b: "We own, monitor and maintain the asset so your team never manages it." },
    { icon: FileText, t: "Transparent billing", b: "One agreed cents-per-kWh rate for on-site energy — no hidden layers." },
  ];
  return (
    <section className="bg-mist py-20 md:py-24" data-testid="proposal-about">
      <div className="container-x mx-auto max-w-7xl grid lg:grid-cols-[1fr_1.2fr] gap-14 items-start">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">About Be Green Group</p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-forest leading-[1.05]">
            A commercial clean-energy partner — not a solar installer.
          </h2>
          <p className="mt-5 text-neutral-600 text-[16px] leading-relaxed">
            Be Green Group helps Australian businesses access cleaner on-site energy without the upfront cost of
            purchasing solar and battery systems. We work with commercial customers, energy specialists, project
            partners and funding partners to deliver simple, usage-based renewable energy solutions.
          </p>
          <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">
            Everything we design is built around commercial load profiles, operating patterns and long-term site
            occupancy — so the system is genuinely fit for how your business runs.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {values.map((v) => (
            <div key={v.t} className="bg-white rounded-2xl border border-black/5 p-6">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-mist text-forest">
                <v.icon size={18} strokeWidth={2.2} />
              </span>
              <h4 className="mt-4 font-heading font-semibold text-forest text-[16px]">{v.t}</h4>
              <p className="mt-2 text-[14px] text-neutral-600 leading-relaxed">{v.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- PROJECTION CHARTS ---------- */
function ProjectionSection({ p, proj }) {
  const rows = proj.rows;
  return (
    <section className="container-x mx-auto max-w-7xl py-20 md:py-24" data-testid="proposal-projection">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">{proj.years}-year projection</p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-forest leading-[1.05] max-w-3xl">
            How your energy costs and savings track over time.
          </h2>
        </div>
        <p className="text-[13px] text-neutral-500 max-w-sm">
          Indicative modelling assumes grid electricity increases at {proj.assumptions.escalation_grid_pct}% p.a. and
          the PPA escalates at {proj.assumptions.escalation_ppa_pct}% p.a., with ~{proj.assumptions.degradation_pct_per_year}% system degradation.
        </p>
      </div>

      {/* Chart 1: Cumulative savings */}
      <div className="rounded-2xl bg-white border border-black/5 p-6 md:p-8 mb-6" data-testid="chart-cum-savings">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h3 className="font-heading text-lg font-semibold text-forest">Cumulative savings</h3>
          <p className="text-[13px] text-neutral-500">Estimated over {proj.years} years</p>
        </div>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <defs>
                <linearGradient id="grad-cum" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(140 80% 40%)" stopOpacity={0.4}/>
                  <stop offset="100%" stopColor="hsl(140 80% 40%)" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false}/>
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#6B7A73" }} tickFormatter={(v) => `Yr ${v}`}/>
              <YAxis tick={{ fontSize: 12, fill: "#6B7A73" }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} width={64}/>
              <Tooltip content={<ChartTooltip formatter={(v) => money(v)} unit=""/>} />
              <Area type="monotone" dataKey="cum_savings" name="Cumulative savings" stroke="hsl(140 80% 30%)" strokeWidth={2.5} fill="url(#grad-cum)"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Chart 2: Grid vs PPA annual cost */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-black/5 p-6 md:p-8" data-testid="chart-grid-vs-ppa">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h3 className="font-heading text-lg font-semibold text-forest">Annual cost — grid vs Be Green</h3>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false}/>
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#6B7A73" }} tickFormatter={(v) => `Yr ${v}`}/>
                <YAxis tick={{ fontSize: 12, fill: "#6B7A73" }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} width={64}/>
                <Tooltip content={<ChartTooltip formatter={(v) => money(v)}/>} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="grid_only_cost" name="Stay on grid" fill="hsl(150 12% 62%)" radius={[6,6,0,0]}/>
                <Bar dataKey="new_annual_cost" name="With Be Green" fill="hsl(140 80% 40%)" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 3: Rate lines */}
        <div className="rounded-2xl bg-white border border-black/5 p-6 md:p-8" data-testid="chart-rates">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h3 className="font-heading text-lg font-semibold text-forest">Effective rate (c / kWh)</h3>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false}/>
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#6B7A73" }} tickFormatter={(v) => `Yr ${v}`}/>
                <YAxis tick={{ fontSize: 12, fill: "#6B7A73" }} tickFormatter={(v) => `${v}c`} width={48}/>
                <Tooltip content={<ChartTooltip formatter={(v) => `${v} c/kWh`}/>} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Line type="monotone" dataKey="grid_rate_cents" name="Grid rate" stroke="hsl(150 12% 45%)" strokeWidth={2.5} dot={false}/>
                <Line type="monotone" dataKey="ppa_rate_cents" name="Be Green PPA" stroke="hsl(140 80% 35%)" strokeWidth={2.5} dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Chart 4: CO2 avoided */}
      <div className="mt-6 rounded-2xl bg-forest text-white p-6 md:p-8 relative overflow-hidden grain" data-testid="chart-co2">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4 relative z-10">
          <div>
            <h3 className="font-heading text-lg font-semibold">Cumulative CO₂ avoided</h3>
            <p className="text-[13px] text-white/60 mt-1">Estimated tonnes of CO₂ avoided by using on-site solar and battery.</p>
          </div>
          <p className="font-heading text-3xl font-semibold text-bright">{proj.totals.total_co2_tonnes} t</p>
        </div>
        <div className="h-64 w-full relative z-10">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <defs>
                <linearGradient id="grad-co2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(140 80% 55%)" stopOpacity={0.5}/>
                  <stop offset="100%" stopColor="hsl(140 80% 55%)" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false}/>
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: "rgba(255,255,255,0.6)" }} tickFormatter={(v) => `Yr ${v}`}/>
              <YAxis tick={{ fontSize: 12, fill: "rgba(255,255,255,0.6)" }} tickFormatter={(v) => `${v} t`} width={54}/>
              <Tooltip content={<ChartTooltip dark formatter={(v) => `${v} tonnes`}/>} />
              <Area type="monotone" dataKey="cum_co2_tonnes" name="Cumulative CO₂ (t)" stroke="hsl(140 80% 60%)" strokeWidth={2.5} fill="url(#grad-co2)"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Year-by-year table */}
      <YearTable rows={rows} />
    </section>
  );
}

function ChartTooltip({ active, payload, label, formatter, dark }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className={`rounded-xl border shadow-lg px-3 py-2 text-[12.5px] ${dark ? "bg-[#0f1f16] border-white/10 text-white" : "bg-white border-black/10 text-forest"}`}>
      <p className={`font-semibold ${dark ? "text-white/80" : "text-forest"}`}>Year {label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="flex items-center gap-2 mt-1">
          <span className="h-2 w-2 rounded-full" style={{ background: entry.color }}/>
          <span className={dark ? "text-white/70" : "text-neutral-600"}>{entry.name}:</span>
          <span className={`font-semibold ${dark ? "text-white" : "text-forest"}`}>{formatter ? formatter(entry.value) : entry.value}</span>
        </p>
      ))}
    </div>
  );
}

function YearTable({ rows }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-8 rounded-2xl bg-mist border border-black/5" data-testid="year-table">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-5 md:p-6"
        data-testid="year-table-toggle"
      >
        <div className="text-left">
          <p className="font-heading font-semibold text-forest">Year-by-year detail</p>
          <p className="text-[12.5px] text-neutral-500">Show the underlying numbers for each year</p>
        </div>
        <ChevronDown size={18} className={`text-forest transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-black/5">
          <table className="min-w-[720px] w-full text-[13.5px]">
            <thead className="bg-white/80 text-[11.5px] uppercase tracking-widest text-neutral-500">
              <tr>
                <Th>Year</Th><Th>Supplied (kWh)</Th><Th>Grid rate</Th><Th>PPA rate</Th>
                <Th>Grid-only cost</Th><Th>Be Green cost</Th><Th>Savings</Th><Th>Cum savings</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.year} className="border-t border-black/5">
                  <Td className="font-semibold text-forest">{r.year}</Td>
                  <Td>{num(r.supplied_kwh)}</Td>
                  <Td>{r.grid_rate_cents}c</Td>
                  <Td>{r.ppa_rate_cents}c</Td>
                  <Td>{money(r.grid_only_cost)}</Td>
                  <Td>{money(r.new_annual_cost)}</Td>
                  <Td className="text-emerald-700 font-semibold">{money(r.annual_savings)}</Td>
                  <Td className="text-forest font-semibold">{money(r.cum_savings)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function Th({ children }) { return <th className="text-left px-4 py-3 font-semibold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

/* ---------- BREAKDOWN (what's included / what you pay) ---------- */
/* ---------- MONTHLY 12-MONTH ECONOMICS ---------- */
function MonthlyEconomicsSection({ proj }) {
  const monthly = proj.monthly || [];
  const stats = proj.monthly_stats || {};
  if (!monthly.length) return null;

  const summerToWinter = stats.winter_avg_kwh > 0
    ? (stats.summer_avg_kwh / stats.winter_avg_kwh).toFixed(1)
    : "—";

  return (
    <section className="bg-mist py-20 md:py-24" data-testid="proposal-monthly">
      <div className="container-x mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">12-month economics · Year 1</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-forest leading-[1.05] max-w-3xl">
              How solar generation moves with the seasons.
            </h2>
          </div>
          <p className="text-[13px] text-neutral-500 max-w-sm">
            Solar produces more in summer than winter for the same system size. This chart
            plots your on-site production month by month against your average monthly usage.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="monthly-stats">
          <StatTile label="Peak month" value={stats.peak_month || "—"} sub={`${num(stats.peak_kwh)} kWh`} tone="bright" testId="stat-peak"/>
          <StatTile label="Low month" value={stats.low_month || "—"} sub={`${num(stats.low_kwh)} kWh`} testId="stat-low"/>
          <StatTile label="Summer avg (D-F)" value={`${num(stats.summer_avg_kwh)} kWh`} sub="per month" testId="stat-summer"/>
          <StatTile label="Winter avg (J-A)" value={`${num(stats.winter_avg_kwh)} kWh`} sub={`${summerToWinter}x ratio summer:winter`} testId="stat-winter"/>
        </div>

        <div className="rounded-2xl bg-white border border-black/5 p-6 md:p-8" data-testid="chart-monthly">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h3 className="font-heading text-lg font-semibold text-forest">Monthly production vs site usage</h3>
            <p className="text-[13px] text-neutral-500">Bars: solar &amp; battery production &middot; Line: site usage</p>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthly} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <defs>
                  <linearGradient id="grad-prod" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(140 80% 45%)"/>
                    <stop offset="100%" stopColor="hsl(140 80% 32%)"/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6B7A73" }}/>
                <YAxis tick={{ fontSize: 12, fill: "#6B7A73" }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} width={54}/>
                <Tooltip content={<ChartTooltip formatter={(v) => `${num(v)} kWh`}/>} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="production_kwh" name="Solar production" fill="url(#grad-prod)" radius={[8, 8, 0, 0]}/>
                <Line type="monotone" dataKey="usage_kwh" name="Site usage" stroke="hsl(150 40% 15%)" strokeWidth={2.5} dot={{ r: 3 }}/>
                <Line type="monotone" dataKey="supplied_from_system_kwh" name="Supplied from system" stroke="hsl(140 60% 40%)" strokeWidth={1.5} strokeDasharray="4 4" dot={false}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-3 text-[12px] text-neutral-500 leading-relaxed">
            Southern-Hemisphere monthly generation share modelled from Australian PVGIS data. Site usage assumed flat by
            default (annual / 12) &mdash; upload interval data or a full year of bills to refine.
          </p>
        </div>
      </div>
    </section>
  );
}
function StatTile({ label, value, sub, tone, testId }) {
  const bright = tone === "bright";
  return (
    <div className={`rounded-2xl border p-4 ${bright ? "bg-bright/10 border-bright/30" : "bg-white border-black/5"}`} data-testid={testId}>
      <p className="text-[10.5px] uppercase tracking-widest font-semibold text-forest/70">{label}</p>
      <p className="mt-1.5 font-heading font-semibold text-forest text-[20px] leading-tight">{value}</p>
      {sub && <p className="mt-0.5 text-[11.5px] text-neutral-500">{sub}</p>}
    </div>
  );
}


function BreakdownSection({ p }) {
  return (
    <section className="bg-mist py-20 md:py-24" data-testid="proposal-breakdown">
      <div className="container-x mx-auto max-w-7xl grid md:grid-cols-2 gap-6">
        <Panel title="What is included">
          <List items={["Commercial solar system", "Battery storage where suitable", "System design", "Installation coordination", "Monitoring", "Maintenance", "Usage-based billing", "Ongoing support"]} />
        </Panel>
        <Panel title="What you pay for">
          <List items={[
            "No upfront system purchase",
            `Flat PPA rate of ${p.proposed_ppa_rate_cents || "—"} c / kWh (indicative)`,
            "Only for energy used from the on-site system",
            "Grid electricity remains billed by your existing retailer",
          ]} accent />
        </Panel>
      </div>
    </section>
  );
}
function Panel({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-7 md:p-8">
      <h3 className="font-heading text-xl md:text-2xl font-semibold text-forest">{title}</h3>
      <div className="mt-5">{children}</div>
    </div>
  );
}
function List({ items, accent }) {
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-[15px] text-neutral-700">
          <span className={`mt-0.5 grid place-items-center h-6 w-6 rounded-full ${accent ? "bg-bright/15" : "bg-mist"} text-forest`}>
            <Sparkles size={12} strokeWidth={2.4} />
          </span>
          {it}
        </li>
      ))}
    </ul>
  );
}

/* ---------- ASSUMPTIONS ---------- */
function Assumptions({ p, proj }) {
  const rows = [
    ["Current annual electricity usage", num(p.current_annual_kwh, " kWh")],
    ["Current estimated grid rate", p.current_avg_rate_cents ? `${p.current_avg_rate_cents} c / kWh` : "—"],
    ["Grid electricity annual escalation", `${proj.assumptions.escalation_grid_pct}%`],
    ["PPA rate annual escalation", `${proj.assumptions.escalation_ppa_pct}%`],
    ["System annual degradation", `${proj.assumptions.degradation_pct_per_year}%`],
    ["CO₂ intensity (grid displaced)", `${proj.assumptions.co2_intensity_kg_per_kwh} kg CO₂ / kWh`],
    ["Proposed PPA term", num(p.proposed_term_years, " years")],
    ["Projection horizon", num(proj.years, " years")],
  ];
  return (
    <section className="container-x mx-auto max-w-7xl py-16 md:py-20" data-testid="proposal-assumptions">
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">Key assumptions</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest leading-[1.05]">The numbers we used.</h2>
          <p className="mt-4 text-neutral-600 text-[15px] leading-relaxed max-w-md">
            These are the modelling inputs used for the projections above. Actual results depend on final site
            assessment, engineering, network conditions and future electricity prices.
          </p>
          {p.assumptions && (
            <div className="mt-6 rounded-2xl bg-mist p-5 border border-black/5">
              <p className="text-[13px] font-semibold text-forest mb-1">Notes from our team</p>
              <p className="whitespace-pre-wrap text-[14px] text-neutral-700 leading-relaxed">{p.assumptions}</p>
            </div>
          )}
        </div>
        <div className="rounded-2xl bg-white border border-black/5 p-6 md:p-8">
          <ul>
            {rows.map(([k, v]) => (
              <li key={k} className="flex items-center justify-between gap-4 py-3 border-b border-black/5 last:border-b-0">
                <span className="text-[13.5px] text-neutral-600">{k}</span>
                <span className="text-[14.5px] font-semibold text-forest text-right">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------- NEXT STEPS ---------- */
function NextSteps({ p }) {
  const steps = [
    { n: "01", t: "Review this proposal with your team.", b: "Take your time — everything on this page is indicative and non-binding." },
    { n: "02", t: "Site inspection & engineering review.", b: "Our team confirms roof, switchboard and metering suitability." },
    { n: "03", t: "Network + credit approval.", b: "We work with your network provider and funding partners." },
    { n: "04", t: "Formal Power Purchase Agreement.", b: "Once approved, we sign the PPA, install and commission the system." },
  ];
  return (
    <section className="container-x mx-auto max-w-7xl pb-16 md:pb-24" data-testid="proposal-next-steps">
      <div className="relative overflow-hidden rounded-3xl bg-forest-gradient text-white grain p-8 md:p-14">
        <div className="relative z-10 grid lg:grid-cols-[1fr_1.1fr] gap-10">
          <div>
            <p className="text-bright text-xs tracking-[0.18em] uppercase font-semibold mb-3">Next steps</p>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold leading-[1.05]">
              Ready to explore this <span className="text-bright">further</span>?
            </h2>
            <p className="mt-5 text-white/75 text-[15.5px] leading-relaxed max-w-xl">
              Nothing on this proposal is binding until a formal PPA is signed. If the numbers look interesting, we
              can arrange a site inspection and prepare a final engineered proposal.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`mailto:hello@begreengroup.com.au?subject=${encodeURIComponent(`Proposal follow-up — ${p.business_name || p.proposal_number}`)}`}
                data-testid="next-cta-email"
                className="inline-flex items-center gap-2 bg-bright text-[hsl(150_40%_8%)] rounded-full px-6 h-12 font-semibold hover:brightness-105 transition"
              >
                Talk to your Be Green contact <ArrowRight size={18} />
              </a>
              <a
                href="tel:1300000000"
                data-testid="next-cta-call"
                className="inline-flex items-center gap-2 rounded-full px-6 h-12 font-medium text-white border border-white/25 hover:bg-white/10 transition"
              >
                Call 1300 000 000
              </a>
            </div>
          </div>
          <div className="grid gap-3">
            {steps.map((s) => (
              <div key={s.n} className="rounded-2xl bg-white/[0.06] border border-white/10 p-5">
                <p className="text-bright font-heading text-[12.5px] font-semibold tracking-widest">{s.n}</p>
                <p className="mt-1 font-heading text-[16px] font-semibold text-white">{s.t}</p>
                <p className="mt-1 text-[13.5px] text-white/65 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- FOOTER ---------- */
function Footer({ p }) {
  return (
    <footer className="border-t border-black/5 bg-white py-10" data-testid="proposal-footer">
      <div className="container-x mx-auto max-w-7xl grid gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3 text-[13px] text-neutral-500">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-8 w-8 rounded-lg bg-forest text-white"><Leaf size={15}/></span>
            <span className="font-heading font-semibold text-forest">Be Green Group</span>
            <span>·  hello@begreengroup.com.au</span>
          </div>
          <span>Proposal · {p.proposal_number}</span>
        </div>
        <p className="text-[12px] text-neutral-500 leading-relaxed">
          <strong>Disclaimer.</strong> This proposal is indicative only and is subject to detailed site inspection,
          engineering design, network approval, credit approval, final pricing, funding approval and execution of a
          formal Power Purchase Agreement. Estimated savings depend on business energy usage, site conditions, system
          design and future electricity prices, none of which are guaranteed.
        </p>
      </div>
    </footer>
  );
}

/* ---------- LOADING / ERROR ---------- */
function FullPageMessage({ title, subtitle }) {
  return (
    <div className="min-h-screen grid place-items-center bg-mist text-center px-6" data-testid="proposal-message">
      <div>
        <span className="grid place-items-center h-14 w-14 rounded-full bg-white border border-black/5 mx-auto">
          <Leaf size={20} className="text-forest"/>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-semibold text-forest">{title}</h1>
        {subtitle && <p className="mt-2 text-neutral-500 max-w-sm mx-auto text-[14px]">{subtitle}</p>}
      </div>
    </div>
  );
}
